import React, { useState, useEffect, useRef } from "react";
import ProjectsOperations from "./component";
import LabelerSnackbar from "../common/SnackBar";
import constants from "../../tools/constants";
import useInterval from "../../tools/useInterval";
import withContext from "../../tools/withContext";
import useRouter from "../../tools/useRouter";
import ProjectType from "../../types/Project";
import UserType from "../../types/User";

// interface ProjectProps {
//     ctx: ContextType;
//     match: any;
// }

const ProjectsOperationContainer = (props: any) => {
  const router = useRouter();
  const {
    ctx: { appFetch },
    match: {
      params: { noPage },
    },
    location: { search },
  } = props;
  const { USERS_PATH, BATCH_GENERATION_PROGRESS_PATH } = constants.paths;
  const [users, setUsers] = useState<UserType[]>([]);
  const [progress, setProgress] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [interval, changeInterval] = useState<number | null>(10000);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [message, setMessage] = useState("");
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      changeInterval(null);
    };
  }, []);

  // on map les projects pour rendre les infos à trier facilement accessibles ( labeler, project )
  const projectMapper = (project: ProjectType, users: any[]) => {
    const admins = users.filter(
      (u: any) =>
        u.projects.includes(project.id) &&
        u.authorities[0] === "ROLE_PROJECT_ADMIN"
    );
    return {
      ...project,
      labelingType: project.labelingTypes[0].name,
      clientStr: (
        <span>
          {project.client.name}
          <br />
          {admins.length > 0 ? (
            <small style={{ color: "gray" }}>{` ${admins
              .map((a: any) => a.lastName)
              .join(", ")}`}</small>
          ) : (
            ""
          )}
        </span>
      ),
    };
  };

  const fetchDatas = async () => {
    try {
      setIsError(false);

      // on récupère les projects
      const usersData: any = await appFetch(USERS_PATH);

      setUsers(usersData.content);
    } catch (e) {
      //, activated: true
      setIsError(true);
      setIsLoading(false);
      // @ts-ignore
      setMessage(e.message);
      setIsSnackbarOpen(true);
    }
  };

  const fetchProgress = async () => {
    if (!isMountedRef.current) return;

    setIsRefreshing(true);
    try {
      const progress = await appFetch(BATCH_GENERATION_PROGRESS_PATH);
      if (!isMountedRef.current) return;

      const progressMapped = progress.reverse().map((prog: any) => ({
        ...prog,
        project: projectMapper(
          prog.project,
          users.filter((u: any) => u.projects.length > 0)
        ),
      }));

      setProgress(progressMapped);
      const shouldStopPolling =
        progressMapped.length === 0 ||
        progressMapped.every((prog: any) => prog.status === "FINISHED");
      if (shouldStopPolling) {
        changeInterval(null);
      } else if (interval === null) {
        changeInterval(search !== "" ? 700 : 3000);
      }
      setIsLoading(false);
    } finally {
      if (isMountedRef.current) {
        setIsRefreshing(false);
      }
    }
  };

  const closeSnackbar = () => setIsSnackbarOpen(false);

  useEffect(() => {
    fetchDatas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noPage, search]);

  useEffect(() => {
    if (interval !== null) {
      changeInterval(search !== "" ? 700 : 3000);
    }
  }, [search, interval]);

  useInterval(() => {
    if (!isRefreshing) {
      fetchProgress();
    }
  }, interval);

  return (
    <React.Fragment>
      <LabelerSnackbar
        open={isSnackbarOpen}
        onClose={closeSnackbar}
        variant={isError ? "error" : "success"}
        message={
          isError
            ? `Une erreur est survenue ${message ? ` : ${message}` : ""}`
            : message
        }
      />
      <ProjectsOperations
        isLoading={isLoading}
        progress={progress}
        appFetch={appFetch}
        onRefresh={() => {
          setIsLoading(true);
          fetchProgress();
        }}
      />
    </React.Fragment>
  );
};

export default withContext(ProjectsOperationContainer);
